import { format } from 'date-fns';
import { DropDown } from 'models/api/dropDown';
import { SelectOption } from 'models/api/prop';
import Id from 'models/id';
import { ColumnType } from 'providers/WorkplaceStateProvider';
import { VersionStatus } from 'models/api/status';

type ColumnConfig = { order?: number; visible: boolean; width?: number | string | null };

export type ColumnsConfig = {
    [key: string]: ColumnConfig;
};

export function columnsToColumnsConfig<T extends Id, TSummaryRow = unknown>(
    columns?: ColumnType<T, TSummaryRow>[]
): ColumnsConfig | undefined {
    if (!columns) {
        return undefined;
    }

    try {
        const initialConfig: ColumnsConfig = {};
        return columns.reduce((res, el, i) => {
            const config: ColumnConfig = {
                order: i,
                visible: el.visible !== false,
                width: el.width,
            };
            res[el.key] = config;
            return res;
        }, initialConfig);
    } catch {
        return undefined;
    }
}

export function configureColumns<T extends Id, TSummaryRow = unknown>(
    columns: ColumnType<T, TSummaryRow>[],
    columnsConfig?: ColumnsConfig
) {
    if (!columnsConfig) {
        return columns;
    }

    try {
        return columns
            .sort(
                (l, r) =>
                    ((columnsConfig[l.key] && columnsConfig[l.key].order) || 0) -
                    ((columnsConfig[r.key] && columnsConfig[r.key].order) || 0)
            )
            .map((x) => ({
                ...x,
                visible: x.notHidden
                    ? x.visible
                    : (columnsConfig[x.key] && columnsConfig[x.key].visible) ?? true,
                width:
                    columnsConfig && columnsConfig[x.key] ? columnsConfig[x.key].width : undefined,
            }));
    } catch (error) {
        console.dir(error);
        return columns;
    }
}

export function unmaskNumberString(value: string) {
    return value.replace(/[^\d]/g, '');
}

export function formatStringByPattern(value: string | null | undefined, pattern: string) {
    if (!value) return '';
    let i = 0;
    return pattern.replace(/#/g, () => value[i++]);
}

export const formatToDateTimeString = (date: string | Date): string => {
    const isoDate = new Date(date).toISOString(); // Преобразует дату в формат ISO 8601
    return isoDate;
  };

export function sharpDateFormatter(
    val: string | null | undefined,
    formatString: string | null | undefined
) {
    if (!val || !formatString) return null;

    return format(new Date(val), formatString);
}

export function translate(code: string, prefix: string, t: (v: string) => string) {
    const val = t(prefix + code);
    if (val === prefix + code || val === '' || val == null) {
        return code;
    }
    return val;
}

export function toMoneyString(money: string | number | null | undefined, culture: string) {
    return Number(money ?? 0).toLocaleString(culture, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

export function toBoolPropString(val: boolean | null) {
    return val?.toString() ?? false.toString();
}

export function boolPropStringToValue(boolProp: string | null) {
    return boolProp === true.toString();
}

export function selectOptionsToDropDown(options: SelectOption[]): DropDown[] {
    return options.map<DropDown>((i) => ({
        id: i.value,
        name: i.text,
        label: i.text,
    }));
}

export const mapBackendStatusToEnum = (backendStatus: string): VersionStatus | null => {
    const foundStatus = Object.values(VersionStatus).find((status) => status === backendStatus.toLowerCase());
    return foundStatus || null;
};

export const formatStatus = (status: VersionStatus): string => {
    return status.charAt(0).toUpperCase() + status.slice(1);
};
